import React, { useState, useEffect } from 'react';
import { FaShoppingCart, FaSearch, FaFilter, FaTimes, FaStar, FaCheck, FaTag } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../common/LoadingSpinner';
import Modal from '../common/Modal';
import { formatCurrency } from '../../utils/helpers';
import { useSubscription } from '../../hooks/useSubscription';

/**
 * Ecommerce shop component for parents
 * Allows browsing and purchasing educational products with subscription-based discounts
 */
const EcommerceShop = () => {
  const { showSuccess, showError } = useToast();
  const { hasTier, getCurrentTier } = useSubscription();
  
  // Products state
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  
  // UI state
  const [showProductModal, setShowProductModal] = useState(false);
  const [showCartModal, setShowCartModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    categories: [],
    ageGroup: [],
    priceRange: {
      min: '',
      max: ''
    },
    inStock: false,
    rating: 0
  });

  // Add discount code state and functionality
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const [discountError, setDiscountError] = useState('');

  // Discount based on subscription tier
  const getDiscountRate = () => {
    const tier = getCurrentTier();
    if (tier === 'plus') return 0.10;  // 10% discount
    if (tier === 'growth') return 0.05; // 5% discount
    return 0;
  };

  // Fetch products on component mount
  useEffect(() => {
    fetchProducts();
    // Load cart from localStorage if it exists
    const savedCart = localStorage.getItem('cartItems');
    if (savedCart) {
      try {
        setCartItems(JSON.parse(savedCart));
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
      }
    }
  }, []);

  // Apply search and filters when they change
  useEffect(() => {
    applyFilters();
  }, [products, searchTerm, filters]);
  
  // Save cart to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
  }, [cartItems]);

  // Fetch products from API
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/ecommerce/products');
      setProducts(response.data.data || []);
      setFilteredProducts(response.data.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      showError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  // Apply search and filters
  const applyFilters = () => {
    let result = [...products];
    
    // Apply search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(
        product => 
          product.name.toLowerCase().includes(search) ||
          product.description.toLowerCase().includes(search)
      );
    }
    
    // Apply category filter
    if (filters.categories.length > 0) {
      result = result.filter(product => 
        filters.categories.includes(product.category)
      );
    }
    
    // Apply age group filter
    if (filters.ageGroup.length > 0) {
      result = result.filter(product => 
        filters.ageGroup.some(age => product.ageGroup.includes(age))
      );
    }
    
    // Apply price range filter
    if (filters.priceRange.min) {
      result = result.filter(product => product.price >= parseFloat(filters.priceRange.min));
    }
    
    if (filters.priceRange.max) {
      result = result.filter(product => product.price <= parseFloat(filters.priceRange.max));
    }
    
    // Apply in-stock filter
    if (filters.inStock) {
      result = result.filter(product => product.inStock);
    }
    
    // Apply rating filter
    if (filters.rating > 0) {
      result = result.filter(product => product.averageRating >= filters.rating);
    }
    
    setFilteredProducts(result);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle filter change
  const handleFilterChange = (filterType, value) => {
    setFilters(prevFilters => {
      if (filterType === 'categories' || filterType === 'ageGroup') {
        // For array filters, toggle the value
        const currentValues = [...prevFilters[filterType]];
        const index = currentValues.indexOf(value);
        
        if (index === -1) {
          currentValues.push(value);
        } else {
          currentValues.splice(index, 1);
        }
        
        return { ...prevFilters, [filterType]: currentValues };
      } else if (filterType === 'priceRange') {
        // For price range object, update specific property
        return { 
          ...prevFilters, 
          priceRange: { ...prevFilters.priceRange, ...value }
        };
      } else if (filterType === 'inStock') {
        // Toggle boolean
        return { ...prevFilters, inStock: !prevFilters.inStock };
      } else {
        // For other filters, just set the value
        return { ...prevFilters, [filterType]: value };
      }
    });
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setFilters({
      categories: [],
      ageGroup: [],
      priceRange: {
        min: '',
        max: ''
      },
      inStock: false,
      rating: 0
    });
  };

  // View product details
  const viewProductDetails = (product) => {
    setSelectedProduct(product);
    setShowProductModal(true);
  };

  // Add product to cart
  const addToCart = (product, quantity = 1) => {
    setCartItems(prev => {
      // Check if product is already in cart
      const existingItem = prev.find(item => item.product._id === product._id);
      
      if (existingItem) {
        // Update quantity if already in cart
        return prev.map(item => 
          item.product._id === product._id 
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        // Add new item to cart
        return [...prev, { product, quantity }];
      }
    });
    
    showSuccess('Product added to cart');
    setShowProductModal(false);
  };

  // Remove item from cart
  const removeFromCart = (productId) => {
    setCartItems(prev => prev.filter(item => item.product._id !== productId));
    showSuccess('Product removed from cart');
  };

  // Update cart item quantity
  const updateCartQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) return;
    
    setCartItems(prev => 
      prev.map(item => 
        item.product._id === productId
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };

  // Validate and apply discount code
  const validateDiscountCode = async () => {
    if (!discountCode.trim()) {
      setDiscountError('Please enter a discount code');
      return;
    }

    try {
      setIsValidatingCode(true);
      setDiscountError('');
      
      // Get the products IDs from cart
      const productIds = cartItems.map(item => item.product._id);
      
      // Calculate cart subtotal before discount
      const subtotal = cartItems.reduce((total, item) => {
        return total + (item.product.price * item.quantity);
      }, 0);

      const response = await api.post('/discounts/validate', {
        code: discountCode,
        products: productIds,
        amount: subtotal,
        // If we're viewing a specific school's products, include the school ID
        ...(selectedProduct && selectedProduct.school && { school: selectedProduct.school }),
      });

      if (response.data.success) {
        setAppliedDiscount({
          code: response.data.data.code,
          percentage: response.data.data.discount
        });
        showSuccess(`Discount code applied: ${response.data.data.discount}% off`);
      } else {
        setDiscountError(response.data.message || 'Invalid discount code');
        setAppliedDiscount(null);
      }
    } catch (error) {
      console.error('Error validating discount code:', error);
      setDiscountError(error.response?.data?.message || 'Error validating discount code');
      setAppliedDiscount(null);
    } finally {
      setIsValidatingCode(false);
    }
  };

  // Remove applied discount
  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
    setDiscountError('');
  };

  // Update calculate cart total to also apply discount codes
  const calculateCartTotal = () => {
    // First calculate with subscription discount
    const subscriptionDiscountRate = getDiscountRate();
    
    const subtotal = cartItems.reduce((total, item) => {
      const itemPrice = item.product.price * item.quantity;
      return total + itemPrice;
    }, 0);
    
    let totalDiscount = subscriptionDiscountRate;
    
    // Apply additional discount code if available
    if (appliedDiscount) {
      totalDiscount += (appliedDiscount.percentage / 100);
    }
    
    // Apply the total discount rate (capped at 100%)
    const effectiveDiscount = Math.min(totalDiscount, 1);
    return subtotal * (1 - effectiveDiscount);
  };

  // Proceed to checkout
  const proceedToCheckout = () => {
    if (cartItems.length === 0) {
      showError('Your cart is empty');
      return;
    }
    
    // Redirect to checkout page
    window.location.href = "/parent/checkout?type=ecommerce";
  };

  // Render star ratings
  const renderRatingStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <FaStar key={i} className={i <= rating ? "text-yellow-500" : "text-gray-300"} />
      );
    }
    return <div className="flex space-x-1">{stars}</div>;
  };

  // Calculate discounted price
  const getDiscountedPrice = (price) => {
    const discountRate = getDiscountRate();
    return price - (price * discountRate);
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <FaShoppingCart className="mr-2 text-primary" /> Educational Shop
          </h1>
          <p className="text-gray-600 mt-1">
            Browse and purchase educational products for your children
          </p>
        </div>
        <div className="mt-4 md:mt-0 flex items-center">
          <button
            onClick={() => setShowCartModal(true)}
            className="relative p-2 text-gray-700 hover:text-primary"
          >
            <FaShoppingCart className="text-xl" />
            {cartItems.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-primary text-white rounded-full text-xs w-5 h-5 flex items-center justify-center">
                {cartItems.length}
              </span>
            )}
          </button>
        </div>
      </div>
      
      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-soft p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          {/* Search Bar */}
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              className="form-input pl-10 py-2 w-full"
              placeholder="Search products..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          
          {/* Filter Toggle */}
          <div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md w-full lg:w-auto"
            >
              <FaFilter className="mr-2" /> 
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
          </div>
        </div>
        
        {/* Expanded Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Categories Filter */}
              <div>
                <h4 className="font-medium mb-2">Categories</h4>
                <div className="space-y-2">
                  {['Books', 'Stationery', 'Educational Games', 'Learning Materials', 'Technology'].map(category => (
                    <label key={category} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filters.categories.includes(category)}
                        onChange={() => handleFilterChange('categories', category)}
                        className="form-checkbox mr-2"
                      />
                      <span>{category}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Age Group Filter */}
              <div>
                <h4 className="font-medium mb-2">Age Group</h4>
                <div className="space-y-2">
                  {['3-5', '6-8', '9-12', '13-15', '16+'].map(age => (
                    <label key={age} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filters.ageGroup.includes(age)}
                        onChange={() => handleFilterChange('ageGroup', age)}
                        className="form-checkbox mr-2"
                      />
                      <span>{age} years</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Price Range Filter */}
              <div>
                <h4 className="font-medium mb-2">Price Range (KES)</h4>
                <div className="flex space-x-2">
                  <input
                    type="number"
                    value={filters.priceRange.min}
                    onChange={(e) => handleFilterChange('priceRange', { min: e.target.value })}
                    className="form-input w-full"
                    placeholder="Min"
                  />
                  <span>-</span>
                  <input
                    type="number"
                    value={filters.priceRange.max}
                    onChange={(e) => handleFilterChange('priceRange', { max: e.target.value })}
                    className="form-input w-full"
                    placeholder="Max"
                  />
                </div>
              </div>
              
              {/* In Stock Filter */}
              <div>
                <h4 className="font-medium mb-2">Availability</h4>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.inStock}
                    onChange={() => handleFilterChange('inStock')}
                    className="form-checkbox mr-2"
                  />
                  <span>In Stock Only</span>
                </label>
              </div>
              
              {/* Rating Filter */}
              <div>
                <h4 className="font-medium mb-2">Minimum Rating</h4>
                <div className="flex items-center space-x-2">
                  <input
                    type="range"
                    min="0"
                    max="5"
                    step="1"
                    value={filters.rating}
                    onChange={(e) => handleFilterChange('rating', parseInt(e.target.value))}
                    className="form-range w-full"
                  />
                  <span>{filters.rating === 0 ? 'Any' : `${filters.rating}+`}</span>
                </div>
              </div>
            </div>
            
            <div className="mt-4 flex justify-end">
              <button
                onClick={resetFilters}
                className="flex items-center px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                <FaTimes className="mr-1" /> Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Subscription-based Discount Banner */}
      {hasTier(['growth', 'plus']) && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center">
          <FaTag className="text-green-600 mr-3 text-xl" />
          <div>
            <h3 className="font-medium text-green-800">Subscription Discount Applied</h3>
            <p className="text-green-700 text-sm">
              {getCurrentTier() === 'plus' 
                ? 'Enjoy 10% off all products with your Plus subscription!'
                : 'Enjoy 5% off all products with your Growth subscription!'}
            </p>
          </div>
        </div>
      )}
      
      {/* Products Grid */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-soft p-8 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-soft">
          {filteredProducts.length === 0 ? (
            <div className="p-8 text-center">
              <FaShoppingCart className="mx-auto text-gray-300 text-5xl mb-4" />
              <h3 className="text-xl font-medium text-gray-600 mb-2">No Products Found</h3>
              <p className="text-gray-500 mb-4">Try adjusting your search criteria or filters</p>
              <button
                onClick={resetFilters}
                className="btn-primary"
              >
                Reset Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 p-6">
              {filteredProducts.map((product) => (
                <div 
                  key={product._id} 
                  className="border border-gray-200 rounded-lg hover:border-primary transition-colors overflow-hidden"
                >
                  <div className="relative h-40 bg-gray-100">
                    {product.image ? (
                      <img 
                        src={product.image} 
                        alt={product.name} 
                        className="w-full h-full object-contain p-2"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FaShoppingCart className="text-gray-400 text-4xl" />
                      </div>
                    )}
                    {!product.inStock && (
                      <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center">
                        <span className="text-white font-medium px-3 py-1 bg-red-500 rounded">Out of Stock</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-4">
                    <h3 className="font-medium text-gray-900 mb-1">{product.name}</h3>
                    <div className="flex items-center mb-2">
                      {renderRatingStars(product.averageRating)}
                      <span className="ml-1 text-xs text-gray-500">({product.reviewCount || 0})</span>
                    </div>
                    
                    <div className="flex justify-between items-center mt-3">
                      <div>
                        {hasTier(['growth', 'plus']) ? (
                          <div>
                            <span className="line-through text-gray-400 text-sm">
                              {formatCurrency(product.price)}
                            </span>
                            <div className="font-bold text-green-600">
                              {formatCurrency(getDiscountedPrice(product.price))}
                            </div>
                          </div>
                        ) : (
                          <div className="font-bold">{formatCurrency(product.price)}</div>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {product.category}
                      </div>
                    </div>
                    
                    <div className="mt-4 flex space-x-2">
                      <button
                        onClick={() => viewProductDetails(product)}
                        className="btn-primary-outline flex-1 py-1 text-sm"
                      >
                        Details
                      </button>
                      <button
                        onClick={() => addToCart(product)}
                        className="btn-primary flex-1 py-1 text-sm"
                        disabled={!product.inStock}
                      >
                        Add to Cart
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Product Details Modal */}
      {selectedProduct && (
        <Modal
          isOpen={showProductModal}
          onClose={() => setShowProductModal(false)}
          title={selectedProduct.name}
          size="lg"
        >
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Product Image */}
              <div className="bg-gray-100 rounded-lg flex items-center justify-center p-4 h-64">
                {selectedProduct.image ? (
                  <img 
                    src={selectedProduct.image} 
                    alt={selectedProduct.name} 
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <FaShoppingCart className="text-gray-400 text-6xl" />
                )}
              </div>
              
              {/* Product Details */}
              <div>
                <div className="flex items-center mb-2">
                  {renderRatingStars(selectedProduct.averageRating)}
                  <span className="ml-2 text-sm text-gray-500">
                    {selectedProduct.averageRating} ({selectedProduct.reviewCount || 0} reviews)
                  </span>
                </div>
                
                <div className="mb-4">
                  <span className="text-sm text-gray-500">Category: {selectedProduct.category}</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {selectedProduct.ageGroup.map((age, idx) => (
                      <span 
                        key={idx} 
                        className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                      >
                        {age} years
                      </span>
                    ))}
                  </div>
                </div>
                
                {hasTier(['growth', 'plus']) ? (
                  <div className="mb-4">
                    <span className="line-through text-gray-400">
                      {formatCurrency(selectedProduct.price)}
                    </span>
                    <div className="text-xl font-bold text-green-600 flex items-center">
                      {formatCurrency(getDiscountedPrice(selectedProduct.price))}
                      <span className="ml-2 text-sm font-normal bg-green-100 text-green-800 px-2 py-1 rounded">
                        {getCurrentTier() === 'plus' ? '10% off' : '5% off'}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="text-xl font-bold mb-4">
                    {formatCurrency(selectedProduct.price)}
                  </div>
                )}
                
                <div className="flex items-center mb-4">
                  <span className={`flex items-center ${selectedProduct.inStock ? 'text-green-600' : 'text-red-600'}`}>
                    {selectedProduct.inStock ? (
                      <>
                        <FaCheck className="mr-1" /> In Stock
                      </>
                    ) : (
                      <>
                        <FaTimes className="mr-1" /> Out of Stock
                      </>
                    )}
                  </span>
                </div>
                
                {selectedProduct.inStock && (
                  <button
                    onClick={() => addToCart(selectedProduct)}
                    className="btn-primary w-full"
                  >
                    Add to Cart
                  </button>
                )}
              </div>
            </div>
            
            <div className="mt-6">
              <h3 className="font-medium mb-2">Description</h3>
              <p className="text-gray-700">{selectedProduct.description}</p>
              
              {selectedProduct.features && selectedProduct.features.length > 0 && (
                <div className="mt-4">
                  <h3 className="font-medium mb-2">Features</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    {selectedProduct.features.map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}
      
      {/* Shopping Cart Modal */}
      <Modal
        isOpen={showCartModal}
        onClose={() => setShowCartModal(false)}
        title="Your Shopping Cart"
        size="lg"
      >
        {cartItems.length === 0 ? (
          <div className="text-center py-8">
            <FaShoppingCart className="mx-auto h-12 w-12 text-gray-300" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Your cart is empty</h3>
            <p className="mt-1 text-sm text-gray-500">
              Browse our catalog to find educational products for your children
            </p>
            <div className="mt-6">
              <button
                onClick={() => setShowCartModal(false)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Continue Shopping
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="flow-root">
              <ul className="divide-y divide-gray-200">
                {cartItems.map((item) => (
                  <li key={item.product._id} className="py-6 flex">
                    <div className="flex-shrink-0 w-24 h-24 border border-gray-200 rounded-md overflow-hidden">
                      {item.product.image ? (
                        <img
                          src={item.product.image}
                          alt={item.product.name}
                          className="w-full h-full object-center object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-100">
                          <FaShoppingCart className="h-8 w-8 text-gray-400" />
                        </div>
                      )}
                    </div>

                    <div className="ml-4 flex-1 flex flex-col">
                      <div>
                        <div className="flex justify-between text-base font-medium text-gray-900">
                          <h3>{item.product.name}</h3>
                          <p className="ml-4">
                            {hasTier(['growth', 'plus']) ? (
                              <>
                                <span className="line-through text-gray-400 text-sm">
                                  {formatCurrency(item.product.price)}
                                </span>
                                <span className="ml-1">
                                  {formatCurrency(getDiscountedPrice(item.product.price))}
                                </span>
                              </>
                            ) : (
                              formatCurrency(item.product.price)
                            )}
                          </p>
                        </div>
                        <p className="mt-1 text-sm text-gray-500">{item.product.category}</p>
                      </div>
                      <div className="flex-1 flex items-end justify-between text-sm">
                        <div className="flex items-center">
                          <button
                            onClick={() => updateCartQuantity(item.product._id, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                            className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
                          >
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4" />
                            </svg>
                          </button>
                          <span className="mx-2">{item.quantity}</span>
                          <button
                            onClick={() => updateCartQuantity(item.product._id, item.quantity + 1)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v12M6 12h12" />
                            </svg>
                          </button>
                        </div>
                        <div className="flex">
                          <button
                            type="button"
                            onClick={() => removeFromCart(item.product._id)}
                            className="font-medium text-red-600 hover:text-red-500"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Discount Code Section */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                <div className="flex-1">
                  <label htmlFor="discount-code" className="sr-only">Discount Code</label>
                  <input
                    type="text"
                    id="discount-code"
                    name="discount-code"
                    placeholder="Enter discount code"
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value)}
                    disabled={!!appliedDiscount || isValidatingCode}
                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  />
                  {discountError && (
                    <p className="mt-1 text-sm text-red-600">{discountError}</p>
                  )}
                </div>
                <div>
                  {appliedDiscount ? (
                    <button
                      type="button"
                      onClick={removeDiscount}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <FaTimes className="mr-2" /> Remove
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={validateDiscountCode}
                      disabled={isValidatingCode || !discountCode.trim()}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-300 disabled:cursor-not-allowed"
                    >
                      {isValidatingCode ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Applying...
                        </>
                      ) : (
                        'Apply'
                      )}
                    </button>
                  )}
                </div>
              </div>
              
              {appliedDiscount && (
                <div className="mt-2 py-2 px-3 bg-green-50 border border-green-100 rounded-md flex items-center justify-between">
                  <div className="flex items-center">
                    <FaCheck className="text-green-500 mr-2" />
                    <span className="text-sm text-green-700">
                      Discount code <span className="font-medium">{appliedDiscount.code}</span> applied ({appliedDiscount.percentage}% off)
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 py-4 mt-4">
              <div className="flex justify-between text-base font-medium text-gray-900">
                <p>Subtotal</p>
                <p>{formatCurrency(calculateCartTotal())}</p>
              </div>
              
              {/* Show discount breakdown */}
              {(hasTier(['growth', 'plus']) || appliedDiscount) && (
                <div className="mt-1 text-sm text-gray-500">
                  <div className="flex justify-between">
                    <p>Discount applied:</p>
                    <p>
                      {hasTier(['growth', 'plus']) && (
                        <span>{getCurrentTier() === 'plus' ? '10% ' : '5% '} (subscription)</span>
                      )}
                      {hasTier(['growth', 'plus']) && appliedDiscount && (
                        <span> + </span>
                      )}
                      {appliedDiscount && (
                        <span>{appliedDiscount.percentage}% (code: {appliedDiscount.code})</span>
                      )}
                    </p>
                  </div>
                </div>
              )}
              
              <p className="mt-2 text-sm text-gray-500">
                Shipping and taxes calculated at checkout.
              </p>
              <div className="mt-4">
                <button
                  onClick={proceedToCheckout}
                  className="w-full flex justify-center items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Checkout
                </button>
              </div>
              <div className="mt-4 flex justify-center text-sm text-gray-500">
                <p>
                  <button
                    type="button"
                    className="text-indigo-600 font-medium hover:text-indigo-500"
                    onClick={() => setShowCartModal(false)}
                  >
                    Continue Shopping
                    <span aria-hidden="true"> &rarr;</span>
                  </button>
                </p>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EcommerceShop;
